<template>
  <main class="pt-3">
    <div class="flex lg:items-start items-center my-3 lg:mt-0 lg:mb-3 w-full">
      <div class="border-b flex items-end w-full">
        <div
          class="flex items-center gap-x-2 lg:gap-x-3 font-light text-xs lg:text-sm"
        >
          <div
            class="py-2 cursor-pointer px-7 transition-all duration-150 ease-linear"
            :class="activeClass('ongoing')"
            @click="setActiveTab('ongoing')"
            test-id="bulkPayoutCategory"
          >
            Ongoing
          </div>
          <div
            class="py-2 cursor-pointer px-5"
            :class="activeClass('history')"
            @click="setActiveTab('history')"
            test-id="bulkPayoutCategory"
          >
            History
          </div>
        </div>

        <!-- search functionality -->
        <div class="hidden md:block md:pb-3 pb-0 ml-auto">
          <div class="lg:w-302px border rounded-lg py-0.5">
            <el-input
              class="w-full outline-none border-none"
              v-model="search"
              @input="searchFilterHandler"
              placeholder="Search"
              debounce
            >
              <i
                slot="prefix"
                :size="40"
                class="el-input__icon el-icon-search"
              ></i>
            </el-input>
          </div>
        </div>
      </div>
    </div>
    <div>
      <div
        class="flex mb-2 lg:mb-8 pb-2 border-b flex-col lg:flex-row lg:items-center w-full"
      >
        <div class="hidden lg:flex items-center mb-4 lg:mb-0 overflow-x-auto">
          <button
            v-for="(category, index) in transactionCategories"
            :key="index"
            :class="[
              category.isActive
                ? 'border border-primary'
                : ' border-main hover-animation-accent-subtle'
            ]"
            class="text-fg-muted font-normal text-13px mr-1 flex py-1 px-2 items-center rounded-lg whitespace-nowrap"
            @click="setCategoryStatus(category)"
          >
            <p class="mr-1" :class="{ 'text-primary': category.isActive }">
              {{ category.name }}
            </p>
            <p
              :class="[
                category.isActive ? 'text-primary bg-secondary-2' : 'bg-gray-30'
              ]"
              class="py-1 px-2.5 rounded-2xl"
            >
              {{ category.count || 0 }}
            </p>
          </button>
        </div>
        <div class="flex lg:ml-auto">
          <!-- Dropdown Currency Filter Implementation -->
          <div class="flex gap-x-3 items-center ml-auto">
            <div class="relative w-full flex items-center justify-end">
              <div>
                <button
                  @click="toggleDropdown"
                  class="flex items-center pl-2 pr-3 md:pl-4 md:pr-5 py-2 lg:py-1.5 rounded-lg border"
                >
                  <currency-icon
                    :currency="getCurrencyCountry(filter.currency)"
                  />
                  <p class="hidden xl:block mx-1.5 text-13px font-normal">
                    {{ filter.currency || 'Currency' }}
                  </p>
                  <arrow-down-icon />
                </button>
              </div>
              <!-- Options for the dropdown -->
              <div
                v-if="dropdownIsVisible"
                test-id="dropdown"
                class="absolute top-12 bg-white border rounded-lg shadow-2xl px-2 py-3 h-48 w-full overflow-scroll z-10"
              >
                <div
                  v-for="(currency, index) in filteredCurrencyOptions"
                  :key="index"
                  class="cursor-pointer"
                >
                  <div
                    @click="setCurrencyFilter(currency.currency)"
                    :class="{
                      'bg-muted': filter.currency === currency.currency
                    }"
                    class="hover:bg-bg-muted rounded-lg flex items-center mb-2 px-3 py-2 font-normal text-sm"
                  >
                    <currency-icon
                      :currency="getCurrencyCountry(currency.currency)"
                    />
                    <p class="ml-1.5">{{ currency.currency }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- End of Dropdown Currency Filter Implementation -->
          <div class="flex gap-x-3 items-center lg:ml-auto">
            <div
              class="flex items-center gap-x-1 border border-root-3 text-root-2 px-3.5 xl:px-2.5 xl:py-1.5 lg:py-2 py-2.5 rounded-lg cursor-pointer"
              @click="showDialog('showTableFilter')"
            >
              <filter-icon />
              <p class="text-black-2 text-sm px-2 font-normal">Filter</p>
              <div class="">
                <arrow-down-icon />
              </div>
            </div>
            <div @click="refreshTable" class="cursor-pointer">
              <refresh-icon />
            </div>
            <!-- The id in the next line maps the walkthrough to that el-button element. Please do not remove this id -->
            <el-button
              id="bulkPayoutTourItem1"
              @click="goToNewBulkPayout"
              class="px-4 w-full py-2.5 flex items-center justify-center gap-x-2 bg-primary text-white shadow-sm rounded-lg font-light"
              :disabled="!hasDepositAccess"
            >
              <div class="flex items-center gap-x-2">
                <add-icon />

                <span>New Payout</span>
              </div>
            </el-button>
          </div>
        </div>
      </div>
      <bulk-payout-table
        v-if="tabIsActive('ongoing')"
        :isLoading="isLoading"
        :table-data="ongoingBulkPayoutsTableData"
        :table-type="activeTable"
        :page="page"
        @sort="sortHandler"
        :sort-direction="sortDirection"
        @handleCurrentChange="handleCurrentChange"
      />
      <bulk-payout-table
        v-if="tabIsActive('history')"
        :isLoading="isLoading"
        :table-data="historyBulkPayoutsTableData"
        :table-type="activeTable"
        :page="page"
        @sort="sortHandler"
        :sort-direction="sortDirection"
        @handleCurrentChange="handleCurrentChange"
      />
    </div>
    <div class="p-6 grid grid-cols-1 md:justify-items-end">
      <!-- dialogs -->
      <filter-dialog
        :showDialog="showTableFilter"
        @filterParams="searchFilterParams"
        @closeDialog="closeDialog('showTableFilter')"
        @cancel="handleCancel"
      />
    </div>
  </main>
</template>

<script>
import notification from '@/mixins/notification.js'
import userKinds from '@/mixins/userKinds.js'
import filteredDepositAccounts from '@/mixins/getBalance.js'
import { GetCurrencyCountry } from '@/utils/helpers'

export default {
  mixins: [notification, userKinds, filteredDepositAccounts],
  components: {
    BulkPayoutTable: () =>
      import('@/components/v2/bulkPayout/tables/BulkPayoutTable.vue'),
    ArrowDownIcon: () => import('@/assets/v2/icons/ArrowDownTwo.vue'),
    FilterIcon: () => import('@/assets/v2/icons/FilterIcon.vue'),
    AddIcon: () => import('@/assets/v2/icons/AddIcon.vue'),
    FilterDialog: () =>
      import('@/components/v2/bulkPayout/dialogs/FilterDialog.vue'),
    CurrencyIcon: () => import('@/assets/icons/CurrencyIcons.vue'),
    RefreshIcon: () => import('@/assets/v2/icons/RefreshIcon.vue')
  },
  data () {
    return {
      defaultPayload: null,
      activeCategory: 'All BulkPayout',
      search: '',
      sortDirection: 'default',
      payload: {
        status: '',
        size: 10,
        page: 1,
        number: 1,
        type: '',
        from: '',
        to: '',
        currency: ''
      },
      showTableFilter: false,
      activeTable: 'ongoing',
      isLoading: false,
      dropdownIsVisible: false,
      filter: {
        currency: ''
      }
    }
  },
  computed: {
    transactionCategories () {
      return [
        {
          name: 'All Payouts',
          isActive: this.activeCategory === 'All BulkPayout',
          type: '',
          count: this.categoryCount?.total_count
        },
        {
          name: 'One time',
          isActive: this.activeCategory === 'One time',
          type: 'one_time',
          count: this.categoryCount?.one_time
        },
        {
          name: 'Recurring',
          isActive: this.activeCategory === 'Recurring',
          type: 'recurring',
          count: this.categoryCount?.recurring
        }
      ]
    },
    allPayoutData () {
      return this.$store.state?.bulkPayout || {}
    },
    ongoingBulkPayoutsTableData () {
      return this.allPayoutData.ongoingBulkPayouts?.items || []
    },
    historyBulkPayoutsTableData () {
      return this.allPayoutData.historyBulkPayouts?.items || []
    },
    page () {
      if (this.activeTable === 'ongoing') {
        return this.allPayoutData.ongoingBulkPayouts?.page || {}
      }
      return this.allPayoutData.historyBulkPayouts?.page || {}
    },
    categoryCount () {
      if (this.activeTable === 'ongoing') {
        return this.allPayoutData.ongoingBulkPayouts.attributes
      }
      return this.allPayoutData.historyBulkPayouts.attributes
    }
  },
  methods: {
    searchFilterParams (filterParams) {
      this.payload.to = this.$dayjs().format('DD/MM/YYYY')
      if (filterParams.date === '30 days') {
        this.payload.from = this.$dayjs()
          .subtract(30, 'days')
          .format('DD/MM/YYYY')
      } else if (filterParams.date === '7 days') {
        this.payload.from = this.$dayjs()
          .subtract(7, 'days')
          .format('DD/MM/YYYY')
      } else if (filterParams.date === 'custom') {
        this.payload.from = this.$dayjs(filterParams.startDate[0]).format(
          'DD/MM/YYYY'
        )
        this.payload.to = this.$dayjs(filterParams.startDate[1]).format(
          'DD/MM/YYYY'
        )
      } else {
        this.payload.from = ''
        this.payload.to = ''
      }
      this.payload.status = this.tableType
      this.searchFilterHandler()
    },
    handleCancel () {
      this.search = ''
      this.payload = this.defaultPayload
      this.searchFilterHandler()
    },
    setCategoryStatus (category) {
      this.activeCategory = category.name
      this.payload.type = category.type
      this.getBulkPayouts(this.activeTable)
    },
    showDialog (dialog) {
      this[dialog] = true
    },
    closeDialog (dialog) {
      this[dialog] = false
    },
    goToNewBulkPayout () {
      this.$store.dispatch('bulkPayout/setActiveComponent', 'bulk-payout-form')
    },
    tabIsActive (tab) {
      return this.activeTable === tab
    },
    sortHandler (sortObj) {
      if (sortObj.sortFormat === 'time') {
        this.$store.dispatch('bulkPayout/sortTimeHandler', sortObj.direction)
      } else if (sortObj.sortFormat === 'amount') {
        this.$store.dispatch('bulkPayout/sortAmountHandler', sortObj.direction)
      } else if (sortObj.sortFormat === 'name') {
        this.$store.dispatch('bulkPayout/sortNameHandler', sortObj.direction)
      }
    },
    searchFilterHandler () {
      this.payload.search = this.search
      this.payload.status = this.activeTable
      this.getBulkPayouts(this.payload.status)
    },
    async handleToggle () {
      await this.getBulkPayouts('ongoing')
      await this.getBulkPayouts('history')
    },
    async setActiveTab (tab) {
      this.activeTable = tab
      this.payload.page = 1
      await this.getBulkPayouts(tab)
    },
    activeClass (tab) {
      return this.activeTable === tab ? 'active-class' : ''
    },
    async handleCurrentChange (payload) {
      this.payload.page = payload.page
      await this.getBulkPayouts(payload.type)
    },
    async getBulkPayouts (status) {
      this.payload.status = status
      this.isLoading = true
      await this.$store
        .dispatch('bulkPayout/getBulkPayouts', this.payload)
        .then(() => {
          this.isLoading = false
        })
        .catch((error) => {
          this.isLoading = false
          this.handleErrorResponseNotification(error)
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    toggleDropdown () {
      this.dropdownIsVisible = !this.dropdownIsVisible
    },
    getCurrencyCountry (val) {
      return GetCurrencyCountry(val)
    },
    setCurrencyFilter (currency) {
      this.filter.currency = currency
      this.dropdownIsVisible = false
      this.handleCurrency()
    },
    async handleCurrency () {
      this.payload.currency = this.filter.currency
      await this.getBulkPayouts('ongoing')
      await this.getBulkPayouts('history')
    },
    async refreshTable () {
      this.payload = {
        status: '',
        size: 10,
        page: 1,
        number: 1,
        type: '',
        from: '',
        to: '',
        currency: ''
      }
      this.filter.currency = ''
      await this.getBulkPayouts('ongoing')
      await this.getBulkPayouts('history')
    }
  },
  async mounted () {
    this.getAllNotifications()
    await this.getBulkPayouts('ongoing')
    await this.getBulkPayouts('history')
  }
}
</script>

<style scoped>
.active-class {
  border-bottom: 1.5px solid var(--color-primary);
  font-weight: 600;
  @apply text-primary;
}
>>> .el-input__inner {
  border: none !important;
  color: var(--foreground-default);
  height: auto !important;
}
>>> .el-tabs__item.is-active {
  color: var(--color-primary);
}
>>> .el-tabs__active-bar.is-top {
  background: var(--color-primary);
}
>>> .el-tabs__item:hover {
  color: var(--color-primary);
}
>>> .el-tabs__item {
  color: var(--text-4);
}
</style>
